// Standard / Express in the cart drawer and on the /cart page (Michael
// 2026-10-07: the Checkout button shows the exact total). The choice lives in
// the cart attribute _shipping_choice, so Liquid (snippets/cart-shipping)
// draws it on every cart change and the deals program reads it from Shopify's
// cart. Tapping an option:
//   1. redraws the Checkout total at once, from the
//      same table the deals server charges with (@deals/core/shipping), and
//      tells screen readers the new total;
//   2. saves the attribute, then has cardo re-render the cart (Shopify's own
//      numbers replace ours) - only for the newest tap, so quick taps don't
//      flicker back (2026-10-07 review finding 12);
//   3. keeps keyboard focus on the chosen option through the re-render (the
//      whole cart region is swapped; review finding 6).
// For the deals client (review finding 7): shippingSettled() resolves once the
// newest choice is SAVED (not redrawn), and shippingChoice() is the choice the
// page shows while a save is still on its way, so Checkout never sends the
// old one. Both wait at most what the client allows (1 s, within its 2.5 s).
import { shippingRate, SHIPPING_ATTRIBUTE } from '@deals/core/shipping'
import { refreshCart } from '@split-infinity/cardo/cart'
import { formatMoney } from './product-price.js'
import { redrawBasket } from './deal-unlock.js'

let saved = Promise.resolve()
let pending // the choice tapped whose save hasn't finished
let taps = 0
let refocus = null // { name, value } of the option that had focus

/** Resolves once the newest choice is in Shopify's cart (or its save failed). */
export const shippingSettled = () => saved

/** The choice the page shows while its save is on the way; otherwise undefined (the cart says). */
export const shippingChoice = () => pending

/**
 * What the basket's Checkout button adds for shipping, in cents: the choice
 * on its way, else the cart's, priced from the cart's total after deals and
 * without gift cards (as cardo:cart leaves it, below). Nothing shipped, none.
 */
export const shippingCents = (cart) =>
  cart.requires_shipping
    ? shippingRate(pending ?? (cart.attributes?.[SHIPPING_ATTRIBUTE] === 'express' ? 'express' : 'standard'), cart.total_price).cents
    : 0

/**
 * Draw a choice on every summary on the page (drawer and /cart page). The new
 * total is announced only by the status in the section the option was tapped
 * in, so /cart (drawer and page both there) says it once (re-review R3).
 */
function draw(choice, from) {
  let total = null
  document.querySelectorAll('input[data-ship-choice]').forEach((input) => {
    input.checked = input.value === choice
  })
  document.querySelectorAll('[data-ship-order]').forEach((el) => {
    const order = Number(el.dataset.shipOrder)
    const pay = Number(el.dataset.shipPay)
    if (!Number.isFinite(order) || !Number.isFinite(pay)) return
    const rate = shippingRate(choice, order)
    total = formatMoney((pay + rate.cents) / 100)
    el.querySelectorAll('[data-ship-total]').forEach((x) => (x.textContent = total))
  })
  // A status outside the swapped region, so it survives the re-render.
  const status = from?.closest('.shopify-section')?.querySelector('[data-ship-status]')
  if (total && status) status.textContent = `Total ${total}`
}

async function save(choice) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [SHIPPING_ATTRIBUTE]: choice } })
  })
  if (!res.ok) throw new Error(`cart update ${res.status}`)
}

/** Focus the re-rendered option that had focus, if focus fell out of it. */
function restoreFocus() {
  if (!refocus) return
  const active = document.activeElement
  if (active && active !== document.body && active.isConnected) return
  const input = [...document.querySelectorAll('input[data-ship-choice]')].find(
    (x) => x.name === refocus.name && x.value === refocus.value
  )
  input?.focus({ preventScroll: true })
}

document.addEventListener('focusin', (event) => {
  const t = event.target
  refocus = t instanceof HTMLInputElement && t.matches('input[data-ship-choice]') ? { name: t.name, value: t.value } : null
})
document.addEventListener('cardo:render', restoreFocus)

document.addEventListener('change', (event) => {
  const input = event.target
  if (!(input instanceof HTMLInputElement) || !input.matches('input[data-ship-choice]') || !input.checked) return
  const choice = input.value === 'express' ? 'express' : 'standard'
  if (document.activeElement === input) refocus = { name: input.name, value: input.value }
  const mine = ++taps
  pending = choice
  draw(choice, input)
  // One save after another, so the last tap wins.
  saved = saved
    .then(() => save(choice))
    .then(
      () => {
        if (mine === taps) pending = undefined // the cart holds it now
      },
      () => {
        // Not saved: the cart (and so the page, after the redraw) keeps the old choice.
        if (mine === taps) pending = undefined
      }
    )
  // Shopify's own render, for the newest tap only.
  void saved.then(() => {
    if (mine === taps) refreshCart().then(restoreFocus, () => {})
  })
})

// The free-shipping lines count what Shopify's rates count: shipped items
// only. cardo's goal reads cart.total_price, so take never-shipped lines
// (gift cards) off first. Capture phase, before cardo's goal (deal-prices.js
// takes the deal saving off the same way). Gift cards take no discounts.
document.addEventListener(
  'cardo:cart',
  (e) => {
    const cart = e.detail?.cart
    if (!cart || cart.unshipped_off !== undefined) return
    const off = (cart.items || [])
      .filter((i) => i.requires_shipping === false)
      .reduce((n, i) => n + (i.final_line_price ?? i.line_price ?? 0), 0)
    cart.unshipped_off = off
    cart.total_price -= off
  },
  true
)

// Back to a tab whose cart another tab changed (deals client 0.7.0 noticed):
// redraw it, so the choice and totals shown are what checkout will charge.
document.addEventListener('deals:stale', () => {
  pending = undefined
  // The basket lines too, not only the totals: another tab changed the basket
  // (2026-10-11 review; cardo's refreshCart doesn't redraw sections here).
  redrawBasket().catch(() => {})
})
